import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import Toggle from '../../../../components/Toggle';
import ConfigAdminFee from '../ConfigAdminFee';
import ConfigOrderDiscounts from '../ConfigOrderDiscounts';
import useHasPaidOrder from '../../hooks/useHasPaidOrder';
import { TOGGLE_ROWS } from '../../../../constants/paymentOptions';
import './style.scss';

export default function PaymentOptions() {
  const { watch, setValue } = useFormContext() || {};
  const location = useLocation();
  const eid = new URLSearchParams(location?.search).get('id');

  const hasPaidOrder = useHasPaidOrder(eid);

  const formPayAfterClosing = watch ? watch('payAfterClosing') : false;
  const formPayTogether = watch ? watch('payTogether') : true;
  const formAdminCost = watch ? watch('adminCost') : '';
  const formOrderDiscountEnabled = watch ? watch('orderDiscountEnabled') : false;
  const formOrderDiscount = watch ? watch('orderDiscount') : null;

  const [expanded, setExpanded] = useState(false);
  const [values, setValues] = useState({
    paymentAfterClosing: Boolean(formPayAfterClosing),
    payTogether: formPayTogether !== undefined ? Boolean(formPayTogether) : true,
    adminFeePerOrder: Boolean(formAdminCost !== '' && Number(formAdminCost) > 0),
    orderDiscounts: Boolean(formOrderDiscountEnabled || formOrderDiscount),
    groupMilestoneDiscount: false,
  });

  useEffect(() => {
    setValues((prev) => {
      const newAdminFee =
        formAdminCost !== '' && Number(formAdminCost) > 0
          ? true
          : prev.adminFeePerOrder;
      const newPayAfterClosing =
        formPayAfterClosing !== undefined
          ? Boolean(formPayAfterClosing)
          : prev.paymentAfterClosing;
      const newPayTogether =
        formPayTogether !== undefined
          ? Boolean(formPayTogether)
          : prev.payTogether;
      const newOrderDiscounts =
        formOrderDiscountEnabled || formOrderDiscount
          ? true
          : prev.orderDiscounts;

      if (
        prev.adminFeePerOrder !== newAdminFee ||
        prev.paymentAfterClosing !== newPayAfterClosing ||
        prev.payTogether !== newPayTogether ||
        prev.orderDiscounts !== newOrderDiscounts
      ) {
        return {
          ...prev,
          adminFeePerOrder: newAdminFee,
          paymentAfterClosing: newPayAfterClosing,
          payTogether: newPayTogether,
          orderDiscounts: newOrderDiscounts,
        };
      }
      return prev;
    });
  }, [
    formAdminCost,
    formPayAfterClosing,
    formPayTogether,
    formOrderDiscountEnabled,
    formOrderDiscount,
  ]);

  useEffect(() => {
    if (setValue) {
      setValue('payAfterClosing', Boolean(values.paymentAfterClosing));
      setValue('payTogether', Boolean(values.payTogether));
      setValue('orderDiscountEnabled', Boolean(values.orderDiscounts));
      if (!values.adminFeePerOrder) {
        setValue('adminCost', '');
      }
    }
  }, [
    values.paymentAfterClosing,
    values.payTogether,
    values.orderDiscounts,
    values.adminFeePerOrder,
    setValue,
  ]);

  const handleToggle = (key, next) => {
    if (key === 'orderDiscounts' && hasPaidOrder) return;
    setValues((prev) => ({ ...prev, [key]: next }));
  };

  return (
    <div className="payment-options">
      <button
        type="button"
        className="payment-options__header"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <span className="payment-options__title">Payment options</span>
        <svg
          className={`payment-options__chevron ${
            expanded ? 'payment-options__chevron--up' : ''
          }`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 10L8 6L12 10"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        className="payment-options__body"
        style={{ display: expanded ? 'block' : 'none' }}
      >
        {TOGGLE_ROWS.map((row) => (
          <div className="option-row" key={row.key}>
            <div className="option-row__top">
              <div className="option-row__text">
                <div className="option-row__title">{row.title}</div>
                <div className="option-row__description">{row.description}</div>
              </div>
              <Toggle
                checked={values[row.key]}
                onChange={(next) => handleToggle(row.key, next)}
                label={row.title}
                disabled={row.key === 'orderDiscounts' && hasPaidOrder}
              />
            </div>
            {row.key === 'adminFeePerOrder' && values.adminFeePerOrder && <ConfigAdminFee />}
            {row.key === 'orderDiscounts' && values.orderDiscounts && (
              <ConfigOrderDiscounts hasPaidOrder={hasPaidOrder} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
